import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";
import { authApi } from "../lib/authApi";
import { showError, showSuccess } from "../lib/toast";

interface FormState {
  email: string;
}

interface FieldError {
  email?: string;
  general?: string;
}

const ForgotPasswordPage = () => {
  const [form, setForm] = useState<FormState>({ email: "" });
  const [errors, setErrors] = useState<FieldError>({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validate = (): boolean => {
    const e: FieldError = {};
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(form.email))
      e.email = "Enter a valid email address.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await authApi.forgotPassword(form.email);
      setSubmitted(true);
      showSuccess("Password reset link sent to your email!");
    } catch (err: unknown) {
      const error = err as Error;
      setErrors({
        general:
          error.message || "Failed to send reset email. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="relative w-full max-w-sm">
          {/* Animated Background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 rounded-full bg-orange-500/20 animate-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  animationDuration: `${3 + Math.random() * 4}s`,
                }}
              />
            ))}
          </div>

          <div className="relative p-5 bg-white border border-gray-100 shadow-lg rounded-xl animate-fade-in">
            <div className="inline-flex items-center justify-center w-12 h-12 mb-3 shadow-md bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <h2 className="mb-1 text-base font-bold text-gray-900">
              Check your email!
            </h2>
            <p className="text-[11px] text-gray-500 mb-1">
              We've sent a password reset link to:
            </p>
            <p className="text-[11px] font-semibold text-gray-900 mb-3 break-all">
              {form.email}
            </p>
            <div className="flex flex-col gap-2">
              <Link
                to="/login"
                className="w-full py-1.5 text-[11px] font-semibold text-center text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
      <div className="relative w-full max-w-sm">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-orange-500/20 animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${3 + Math.random() * 4}s`,
              }}
            />
          ))}
        </div>

        {/* Main Card */}
        <div className="relative overflow-hidden py-4 bg-white border border-gray-100 shadow-lg rounded-xl">
          {/* Header - Compact */}
          <div className="px-4 py-3 text-center bg-gradient-to-r from-orange-600 to-orange-500">
            <div className="flex items-center justify-center gap-0 font-bold text-center">
              <span className="text-white text-[1.2rem]">Learn</span>
              <span className="text-white text-[1.2rem]">Hub</span>
            </div>
            <p className="mt-1 text-[10px] text-orange-100">Reset password</p>
          </div>

          {/* Content - Compact */}
          <div className="p-4">
            {/* Header Text */}
            <div className="mb-3">
              <p className="text-[11px] text-gray-500">
                Remember your password?{" "}
                <Link
                  to="/login"
                  className="font-semibold text-orange-500 hover:text-orange-600"
                >
                  Sign in
                </Link>
              </p>
            </div>

            {/* General error */}
            {errors.general && (
              <div className="flex items-center gap-2 p-2 mb-3 border border-red-200 rounded-lg bg-red-50">
                <AlertCircle className="w-3 h-3 text-red-500 shrink-0" />
                <p className="text-[10px] font-medium text-red-600">
                  {errors.general}
                </p>
              </div>
            )}

            {/* Info text */}
            <div className="p-2 mb-3 border border-blue-100 rounded-lg bg-blue-50">
              <p className="text-[10px] text-blue-700 leading-relaxed">
                No worries! Enter your email address and we'll send you a link
                to reset your password.
              </p>
            </div>

            {/* Email Field */}
            <div className="mb-3">
              <label className="block mb-0.5 text-[10px] font-semibold text-gray-600">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => {
                    setForm({ email: e.target.value });
                    setErrors({});
                  }}
                  className={`w-full pl-7 pr-2 py-2.5 text-[11px] rounded-lg border bg-white transition-all outline-none
                    focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400
                    ${errors.email ? "border-red-300 bg-red-50" : "border-gray-200 hover:border-gray-300"}`}
                />
              </div>
              {errors.email && (
                <p className="mt-0.5 text-[9px] text-red-500">{errors.email}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-2.5 mt-2 text-[11px] font-semibold text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all disabled:opacity-50"
            >
              {loading ? (
                <>
                  <svg
                    className="w-3 h-3 text-white animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                  Sending...
                </>
              ) : (
                "Send Reset Link"
              )}
            </button>

            {/* Back to login link */}
            <div className="mt-3 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-[9px] text-gray-500 transition-colors hover:text-orange-500"
              >
                <ArrowLeft className="w-2.5 h-2.5" />
                Back to login
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-3 text-center text-[9px] text-gray-400">
          Password reset link expires in 24 hours
        </p>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-20px) translateX(10px); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ForgotPasswordPage;
